import { jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import Draggable from "react-draggable";
import ReactList from "@teselagen/react-list";
import Axis from "../RowItem/Axis";
import getXStartAndWidthFromNonCircularRange from "../RowItem/getXStartAndWidthFromNonCircularRange";
import { view } from "@risingstack/react-easy-state";
import { flatMap, some, toNumber } from "lodash-es";
import {
  getOverlapOfNonCircularRanges,
  invertRange,
  isPositionWithinRange,
  splitRangeIntoTwoPartsIfItIsCircular
} from "@teselagen/range-utils";
import { massageTickSpacing } from "../utils/massageTickSpacing";
import { getClientX, getClientY } from "../utils/editorUtils";
export default class Minimap extends React.Component {
  shouldComponentUpdate(newProps) {
    const { props } = this;
    if ([
      "alignmentTracks",
      "numBpsShownInLinearView",
      "scrollAlignmentView",
      "laneHeight",
      "laneSpacing",
      "isTrackSelected"
    ].some((key) => props[key] !== newProps[key]))
      return true;
    return false;
  }
  handleMinimapClick = (e) => {
    if (this.isDragging || e.target && e.target.classList.contains("minimapCaret")) {
      e.stopPropagation();
      return;
    }
    const {
      onMinimapScrollX,
      dimensions: { width = 200 }
    } = this.props;
    const scrollHandleWidth = this.getScrollHandleWidth();
    const percent = (this.getXPositionOfClickInMinimap(e) - scrollHandleWidth / 2) / (width - scrollHandleWidth);
    onMinimapScrollX(percent);
    this.scrollMinimapVertical({ e, force: true });
  };
  getSeqLen = () => {
    const { alignmentTracks = [] } = this.props;
    const [template] = alignmentTracks;
    const seqLength = template.alignmentData.sequence.length;
    return seqLength;
  };
  /**
   * @returns current nucleotide char
   * width, nucelotide char width scales with zooming
   */
  getCharWidth = () => {
    const {
      dimensions: { width = 200 }
    } = this.props;
    const seqLength = this.getSeqLen();
    const charWidth = Math.min(16, width / seqLength);
    return charWidth || 12;
  };
  /**
   * @returns the width of the highlighted region of the minimap
   */
  getScrollHandleWidth = () => {
    const { numBpsShownInLinearView, dimensions } = this.props;
    const charWidth = this.getCharWidth();
    const { width } = getXStartAndWidthFromNonCircularRange(
      { start: 0, end: Math.max(numBpsShownInLinearView - 1, 0) },
      charWidth
    );
    return Math.min(width, dimensions.width);
  };
  getXPositionOfClickInMinimap = (e) => {
    const leftStart = this.minimap.getBoundingClientRect().left;
    return Math.max(getClientX(e) - leftStart, 0);
  };
  getYPositionOfClickInMinimap = (e) => {
    const topStart = this.minimap.getBoundingClientRect().top;
    return Math.max(getClientY(e) + this.minimapTracks.scrollTop - topStart, 0);
  };
  scrollMinimapVertical = ({ e, force, initialDragYOffsetFromCenter }) => {
    const clientY = getClientY(e) - (initialDragYOffsetFromCenter || 0);
    try {
      if (!force && isPositionWithinRange(clientY, {
        start: this.lastYPosition - 5,
        end: this.lastYPosition + 5
      })) {
        return;
      }
      const lanes = document.querySelectorAll(".minimapLane");
      some(lanes, (lane) => {
        const rect = lane.getBoundingClientRect();
        if (rect.top > clientY && rect.top - rect.height < clientY) {
          const laneI = toNumber(lane.getAttribute("data-lane-index"));
          let scrollToLane = laneI - 3;
          if (laneI === lanes.length - 1) {
            scrollToLane = laneI - 1;
          } else if (laneI === lanes.length - 2) {
            scrollToLane = laneI - 2;
          }
          this.props.scrollYToTrack(Math.max(scrollToLane, 0));
          return true;
        }
        return false;
      });
      this.lastYPosition = clientY;
    } catch (error) {
      console.error(`error in scrollMinimapVertical:`, error);
    }
  };
  handleDragStop = () => {
    setTimeout(() => {
      this.isDragging = false;
    }, 150);
  };
  /**
   * Handler for beginning to drag across the minimap
   * Sets this.initialDragXOffsetFromCenter and Y for dragging
   * @param {*} e - event
   */
  handleDragStart = (e) => {
    const eventX = e.pageX;
    const handleEl = window.document.querySelector(".verticalScrollDisplay");
    if (!handleEl) return;
    const { x, width } = handleEl.getBoundingClientRect();
    const yellowScrollHandleXCenter = x + width / 2;
    this.initialDragXOffsetFromCenter = eventX - yellowScrollHandleXCenter;
    const eventY = e.pageY;
    if (!handleEl) return;
    const { y, height } = handleEl.getBoundingClientRect();
    const yellowScrollHandleYCenter = y + height / 2;
    this.initialDragYOffsetFromCenter = eventY - yellowScrollHandleYCenter;
  };
  /**
   * Moves the highlighted region as we drag
   * @param {*} e - event
   */
  handleDrag = (e) => {
    const {
      onMinimapScrollX,
      dimensions: { width = 200 }
    } = this.props;
    this.isDragging = true;
    const scrollHandleWidth = this.getScrollHandleWidth();
    const percent = (this.getXPositionOfClickInMinimap(e) - (this.initialDragXOffsetFromCenter || 0) - scrollHandleWidth / 2) / (width - scrollHandleWidth);
    onMinimapScrollX(percent);
    this.scrollMinimapVertical({
      e,
      initialDragYOffsetFromCenter: this.initialDragYOffsetFromCenter
    });
  };
  /**
   * @returns this.props.laneheight
   */
  itemSizeGetter = () => {
    return this.props.laneHeight;
  };
  /**
   * Renders a lane (one by one for each call)
   * @param {*} i - lane info
   */
  renderItem = (i) => {
    const {
      alignmentTracks = [],
      dimensions: { width = 200 },
      laneHeight,
      laneSpacing = 1,
      isTrackSelected = []
    } = this.props;
    const charWidth = this.getCharWidth();
    const {
      matchHighlightRanges: _matchHighlightRanges,
      alignmentData: { trimmedRange } = {}
    } = alignmentTracks[i];
    const matchHighlightRanges = !trimmedRange ? _matchHighlightRanges : flatMap(_matchHighlightRanges, (r) => {
      const overlap = getOverlapOfNonCircularRanges(r, trimmedRange);
      if (!overlap) return [];
      return { ...r, ...overlap };
    });
    let redPath = "";
    let bluePath = "";
    const height = laneHeight - laneSpacing;
    const y = 0;
    const firstRange = getXStartAndWidthFromNonCircularRange(
      matchHighlightRanges[0],
      charWidth
    );
    const lastRange = getXStartAndWidthFromNonCircularRange(
      matchHighlightRanges[matchHighlightRanges.length - 1],
      charWidth
    );
    bluePath += `M${firstRange.xStart},${y} L${lastRange.xStart + lastRange.width},${y} L${lastRange.xStart + lastRange.width},${y + height} L${firstRange.xStart},${y + height}`;
    matchHighlightRanges.forEach((range) => {
      const { xStart, width: width2 } = getXStartAndWidthFromNonCircularRange(
        range,
        charWidth
      );
      const toAdd = `M${xStart},${y} L${xStart + width2},${y} L${xStart + width2},${y + height} L${xStart},${y + height}`;
      if (!range.isMatch) {
        redPath += toAdd;
      }
    });
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: `minimapLane ${isTrackSelected[i] ? "isTrackSelected" : ""}`,
        "data-lane-index": i,
        style: { height: laneHeight, maxHeight: laneHeight },
        children: /* @__PURE__ */ jsxs(
          "svg",
          {
            height: laneHeight,
            width,
            shapeRendering: "geometricPrecision",
            children: [
              /* @__PURE__ */ jsx("path", { className: "miniBluePath", d: bluePath, fill: "#9abeff" }),
              /* @__PURE__ */ jsx("path", { className: "miniRedPath", d: redPath, fill: "#FF4444" })
            ]
          }
        )
      },
      i + "-lane"
    );
  };
  /**
   * @returns minimap compoent
   */
  render() {
    const {
      alignmentTracks = [],
      dimensions: { width = 200 },
      laneHeight,
      onSizeAdjust,
      minSliderSize,
      onMinimapScrollX,
      easyStore,
      selectionLayerComp
    } = this.props;
    const [
      template
      /* ...nonTemplates */
    ] = alignmentTracks;
    const seqLength = template.alignmentData.sequence.length;
    const charWidth = this.getCharWidth();
    const scrollHandleWidth = this.getScrollHandleWidth();
    const minimapTracksPartialHeight = laneHeight * alignmentTracks.length;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: (ref) => this.minimap = ref,
        className: "alignmentMinimap",
        style: {
          position: "relative",
          width,
          display: "flex",
          flexDirection: "column",
          overflowX: "visible"
        },
        onClick: this.handleMinimapClick,
        children: [
          selectionLayerComp,
          /* @__PURE__ */ jsxs(
            "div",
            {
              ref: (ref) => {
                if (ref) {
                  this.minimapTracks = ref;
                }
              },
              style: {
                overflowY: minimapTracksPartialHeight > 190 ? "auto" : "hidden",
                overflowX: "hidden",
                position: "relative"
              },
              className: "alignmentMinimapTracks",
              children: [
                /* @__PURE__ */ jsx(
                  YellowScrollHandle,
                  {
                    width,
                    handleDragStart: this.handleDragStart,
                    handleDrag: this.handleDrag,
                    handleDragStop: this.handleDragStop,
                    onMinimapScrollX,
                    minSliderSize,
                    onSizeAdjust,
                    easyStore,
                    scrollHandleWidth,
                    alignmentTracks,
                    laneHeight,
                    minimapTracksPartialHeight
                  }
                ),
                /* @__PURE__ */ jsx(
                  ReactList,
                  {
                    itemsRenderer: (items, ref) => /* @__PURE__ */ jsx(
                      "div",
                      {
                        style: {
                          marginTop: -3
                        },
                        ref,
                        children: items
                      }
                    ),
                    type: "uniform",
                    itemSizeGetter: this.itemSizeGetter,
                    itemRenderer: this.renderItem,
                    length: alignmentTracks.length
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Axis,
            {
              ...{
                row: { start: 0, end: seqLength - 1 },
                tickSpacing: massageTickSpacing(Math.floor(seqLength / 10)),
                bpsPerRow: seqLength,
                charWidth,
                annotationHeight: 15,
                sequenceLength: seqLength,
                style: {
                  height: 17,
                  width: "100%"
                }
              }
            }
          )
        ]
      }
    );
  }
}
const YellowScrollHandle = view(
  class YellowScrollHandleInner extends React.Component {
    render() {
      const {
        scrollHandleWidth,
        width,
        easyStore,
        handleDrag,
        handleDragStop,
        minSliderSize,
        laneHeight,
        onSizeAdjust,
        handleDragStart,
        minimapTracksPartialHeight
      } = this.props;
      const { verticalVisibleRange, percentScrolled } = easyStore;
      const xScroll = percentScrolled * (width - scrollHandleWidth);
      return /* @__PURE__ */ jsx(
        Draggable,
        {
          bounds: "parent",
          zIndex: 105,
          handle: ".handle",
          position: { x: xScroll, y: 0 },
          axis: "x",
          onStop: handleDragStop,
          onDrag: handleDrag,
          onStart: handleDragStart,
          children: /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                height: minimapTracksPartialHeight || 0,
                // height: "100%",
                border: "none",
                top: "0px",
                position: "absolute",
                zIndex: "10"
              },
              children: [
                /* @__PURE__ */ jsx(
                  Draggable,
                  {
                    bounds: {
                      left: -xScroll,
                      right: scrollHandleWidth - minSliderSize
                    },
                    zIndex: 105,
                    position: { x: 0, y: 0 },
                    axis: "x",
                    onStart: (e, { x }) => {
                      this.x = x;
                    },
                    onStop: (e, { x }) => {
                      const deltaX = x - this.x;
                      const newSliderSize = scrollHandleWidth - deltaX;
                      const newScrollPercent = Math.min(
                        1,
                        (xScroll + deltaX) / (width - newSliderSize)
                      );
                      onSizeAdjust(newSliderSize, newScrollPercent);
                    },
                    children: /* @__PURE__ */ jsx(
                      "div",
                      {
                        style: {
                          height: minimapTracksPartialHeight || 0,
                          // height: "100%",
                          border: "none",
                          cursor: "ew-resize",
                          opacity: "1",
                          top: "0px",
                          position: "absolute",
                          zIndex: "10",
                          width: 2,
                          background: "black"
                        },
                        className: "minimapCaret"
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "handle alignmentMinimapScrollHandle",
                    dataname: "scrollGroup",
                    style: {
                      height: minimapTracksPartialHeight || 0,
                      border: "none",
                      cursor: "move",
                      zIndex: "10",
                      width: scrollHandleWidth,
                      background: "transparent"
                    },
                    children: /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "verticalScrollDisplay",
                        style: {
                          height: (verticalVisibleRange.end - verticalVisibleRange.start + 1) * laneHeight,
                          zIndex: "-10",
                          background: "#fbfb2873",
                          borderTop: "2px solid yellow",
                          borderBottom: "2px solid yellow",
                          position: "relative",
                          top: verticalVisibleRange.start * laneHeight
                        }
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx(
                  Draggable,
                  {
                    bounds: {
                      right: minSliderSize + width - xScroll,
                      left: minSliderSize
                    },
                    zIndex: 105,
                    position: { x: scrollHandleWidth, y: 0 },
                    axis: "x",
                    onStart: (e, { x }) => {
                      this.x = x;
                    },
                    onStop: (e, { x }) => {
                      const deltaX = this.x - x;
                      const newSliderSize = scrollHandleWidth - deltaX;
                      const newScrollPercent = xScroll / (width - newSliderSize);
                      onSizeAdjust(newSliderSize, newScrollPercent);
                    },
                    children: /* @__PURE__ */ jsx(
                      "div",
                      {
                        style: {
                          height: minimapTracksPartialHeight || 0,
                          // height: "100%",
                          border: "none",
                          cursor: "ew-resize",
                          opacity: "1",
                          top: "0px",
                          // right: 0,
                          position: "absolute",
                          zIndex: "10",
                          width: 2,
                          background: "black"
                        },
                        className: "minimapCaret"
                      }
                    )
                  }
                )
              ]
            }
          )
        }
      );
    }
  }
);
export function getTrimmedRangesToDisplay({ trimmedRange, seqLen }) {
  if (!trimmedRange) return [];
  const inverted = invertRange(trimmedRange, seqLen);
  return splitRangeIntoTwoPartsIfItIsCircular(inverted, seqLen);
}
